<script setup>
import { ref, computed, watch, nextTick, onMounted, onUnmounted } from 'vue';

const props = defineProps({
  modelValue: {
    type: String,
    default: '',
  },
  label: {
    type: String,
    default: '',
  },
  placeholder: {
    type: String,
    default: '',
  },
  message: {
    type: String,
    default: '',
  },
  messageType: {
    type: String,
    default: 'info',
    validator: value => ['info', 'error', 'success'].includes(value),
  },
  maxLength: {
    type: Number,
    default: null,
  },
  showCharacterCount: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(['update:modelValue']);

// Variables disponibles para autocompletado
const availableVariables = [
  {
    label: 'Nombre del contacto',
    value: '{{contact.name}}',
  },
  {
    label: 'Teléfono del contacto',
    value: '{{contact.phone_number}}',
  },
  {
    label: 'Nombre de la empresa',
    value: '{{account.name}}',
  },
];

// Estado interno
const textareaRef = ref(null);
const internalValue = ref(props.modelValue);
const showSuggestions = ref(false);
const selectedIndex = ref(-1);
const cursorPosition = ref(0);
const searchTerm = ref('');
const triggerPosition = ref(null);
const isFocused = ref(false);

// Clases CSS computadas
const messageClass = computed(() => {
  switch (props.messageType) {
    case 'error':
      return 'text-n-ruby-9 dark:text-n-ruby-9';
    case 'success':
      return 'text-n-teal-10 dark:text-n-teal-10';
    default:
      return 'text-n-slate-11 dark:text-n-slate-11';
  }
});

// Variables filtradas
const filteredVariables = computed(() => {
  if (!searchTerm.value) return availableVariables;

  return availableVariables.filter(
    variable =>
      variable.label.toLowerCase().includes(searchTerm.value.toLowerCase()) ||
      variable.value.toLowerCase().includes(searchTerm.value.toLowerCase())
  );
});

// Estilo del dropdown
const dropdownStyle = computed(() => {
  if (!triggerPosition.value) return {};

  return {
    top: `${triggerPosition.value.top + 20}px`,
    left: `${triggerPosition.value.left}px`,
  };
});

// Watchers
watch(
  () => props.modelValue,
  newValue => {
    internalValue.value = newValue;
  }
);

watch(internalValue, newValue => {
  emit('update:modelValue', newValue);
});

watch(selectedIndex, newIndex => {
  if (newIndex >= filteredVariables.value.length) {
    selectedIndex.value = 0;
  } else if (newIndex < -1) {
    selectedIndex.value = filteredVariables.value.length - 1;
  }
});

// Métodos - Definidos primero para evitar hoisting issues
const hideSuggestions = () => {
  showSuggestions.value = false;
  searchTerm.value = '';
  triggerPosition.value = null;
};

const getCursorPosition = () => {
  const textarea = textareaRef.value;
  if (!textarea) return { top: 0, left: 0 };

  // Posición simple: siempre mostrar el dropdown un poco abajo del cursor
  return {
    top: 20, // 20px abajo del cursor
    left: 0, // Alineado a la izquierda
  };
};

const insertVariable = variable => {
  const value = internalValue.value;
  const position = cursorPosition.value;

  // Encontrar el inicio del @ trigger
  const textBeforeCursor = value.substring(0, position);
  const lastAtIndex = textBeforeCursor.lastIndexOf('@');

  if (lastAtIndex === -1) return;

  // Reemplazar desde @ hasta cursor con la variable
  const newValue =
    value.substring(0, lastAtIndex) +
    variable.value +
    value.substring(position);

  internalValue.value = newValue;

  // Posicionar cursor después de la variable insertada
  nextTick(() => {
    const newCursorPosition = lastAtIndex + variable.value.length;
    textareaRef.value.setSelectionRange(newCursorPosition, newCursorPosition);
    textareaRef.value.focus();

    // Ocultar sugerencias después del focus
    setTimeout(() => {
      hideSuggestions();
    }, 50);
  });
};

const checkForTrigger = (value, position) => {
  // Buscar el último @ antes de la posición actual
  const textBeforeCursor = value.substring(0, position);
  const lastAtIndex = textBeforeCursor.lastIndexOf('@');

  if (lastAtIndex === -1) {
    hideSuggestions();
    return;
  }

  // Verificar si hay espacios entre @ y cursor
  const textAfterAt = textBeforeCursor.substring(lastAtIndex + 1);
  if (textAfterAt.includes(' ')) {
    hideSuggestions();
    return;
  }

  // Mostrar sugerencias
  searchTerm.value = textAfterAt;
  triggerPosition.value = getCursorPosition();
  showSuggestions.value = true;
  selectedIndex.value = -1;
};

const handleInput = event => {
  const value = event.target.value;
  const position = event.target.selectionStart;

  internalValue.value = value;
  cursorPosition.value = position;

  checkForTrigger(value, position);
};

const handleFocus = () => {
  isFocused.value = true;
};

const handleBlur = () => {
  setTimeout(() => {
    isFocused.value = false;
    hideSuggestions();
  }, 200);
};

const handleKeydown = event => {
  if (!showSuggestions.value) return;

  switch (event.key) {
    case 'ArrowDown':
      event.preventDefault();
      selectedIndex.value =
        selectedIndex.value >= filteredVariables.value.length - 1
          ? 0
          : selectedIndex.value + 1;
      break;
    case 'ArrowUp':
      event.preventDefault();
      selectedIndex.value =
        selectedIndex.value <= 0
          ? filteredVariables.value.length - 1
          : selectedIndex.value - 1;
      break;
    case 'Enter':
    case 'Tab':
      event.preventDefault();
      if (
        selectedIndex.value >= 0 &&
        filteredVariables.value[selectedIndex.value]
      ) {
        insertVariable(filteredVariables.value[selectedIndex.value]);
      }
      break;
    case 'Escape':
      hideSuggestions();
      break;
    default:
      // No hacer nada para otras teclas
      break;
  }
};

const handleClickOutside = event => {
  if (textareaRef.value && !textareaRef.value.contains(event.target)) {
    hideSuggestions();
  }
};

// Lifecycle
onMounted(() => {
  document.addEventListener('click', handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
});
</script>

<template>
  <div class="relative">
    <label v-if="label" class="mb-0.5 text-sm font-medium text-n-slate-12">
      {{ label }}
    </label>

    <!-- Contenedor principal -->
    <div
      class="flex flex-col gap-2 px-3 pt-3 pb-3 transition-all duration-500 ease-in-out border rounded-lg bg-n-alpha-black2"
      :class="[
        {
          'border-n-brand dark:border-n-brand': isFocused,
          'hover:border-n-slate-6 dark:hover:border-n-slate-6 border-n-weak dark:border-n-weak':
            !isFocused && messageType !== 'error',
          'border-n-ruby-8 dark:border-n-ruby-8 hover:border-n-ruby-9 dark:hover:border-n-ruby-9':
            messageType === 'error' && !isFocused,
        },
      ]"
    >
      <!-- Textarea principal -->
      <div class="relative">
        <textarea
          ref="textareaRef"
          v-model="internalValue"
          :placeholder="placeholder"
          :maxlength="maxLength"
          rows="12"
          class="flex w-full reset-base text-sm p-0 !rounded-none !bg-transparent dark:!bg-transparent !border-0 !outline-0 !mb-0 placeholder:text-n-slate-10 dark:placeholder:text-n-slate-10 text-n-slate-12 dark:text-n-slate-12 resize-none !min-h-[160px]"
          @input="handleInput"
          @keydown="handleKeydown"
          @focus="handleFocus"
          @blur="handleBlur"
        />

        <!-- Dropdown de variables -->
        <div
          v-if="showSuggestions"
          :style="dropdownStyle"
          class="absolute z-50 bg-white dark:bg-n-slate-1 border border-n-weak dark:border-n-weak rounded-lg shadow-xl max-h-40 overflow-y-auto min-w-48"
          @mousedown.prevent
        >
          <div
            v-for="(variable, index) in filteredVariables"
            :key="variable.value"
            class="px-3 py-2 cursor-pointer flex flex-col gap-0.5 border-b border-n-weak dark:border-n-weak last:border-b-0"
            :class="[
              index === selectedIndex
                ? 'bg-n-blue-2 dark:bg-n-blue-8'
                : 'hover:bg-n-blue-1 dark:hover:bg-n-blue-9',
            ]"
            @click.stop.prevent="insertVariable(variable)"
          >
            <span
              class="font-medium text-sm text-n-slate-12 dark:text-n-slate-12"
            >
              {{ variable.label }}
            </span>
            <span class="text-xs text-n-slate-11 dark:text-n-slate-11">{{
              variable.value
            }}</span>
          </div>
        </div>
      </div>

      <!-- Contador de caracteres -->
      <div class="flex justify-between items-center">
        <span v-if="message" :class="messageClass" class="text-xs">
          {{ message }}
        </span>
        <div class="flex-1" />
        <span
          v-if="showCharacterCount"
          class="text-xs text-n-slate-11 dark:text-n-slate-11"
        >
          {{ internalValue.length }}{{ maxLength ? `/${maxLength}` : '' }}
        </span>
      </div>
    </div>
  </div>
</template>
